import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { DataTable, StatCard, HandoverFlowModal, HandoverDetailsModal } from "../../../jsx/components/common";
import Pagination from "../permitUsers/Pagination";
import ImageModal from "../../../components/ImageModal";
import { HugeiconsIcon } from "@hugeicons/react";
import { PrinterIcon, ShareIcon, ClockIcon } from "@hugeicons/core-free-icons";
import PageTitle from "../../../jsx/layouts/PageTitle";
import image from "../../../images/pic3.jpg";
import id from "../../../images/id.jpg";
import { 
  enhancedHandoverService,
  getErrorMessage 
} from "../../../services/enhancedAccreditationApiService";
// import SearchableUserTypeDropdown from "../permission/SearchableUserTypeDropdown";
import SearchableUserTypeMultiSelect from "../permission/SearchableUserTypeMultiSelect.jsx";
import SearchableHandoverTypeDropdown from "../permission/SearchableHandoverTypeDropdown";
import SearchableUserFromDropdown from "../permission/SearchableUserFromDropdown";
import SearchableSportDropdown from "../permission/SearchableSportDropdown";
import SearchableCountryDropdown from "../permission/SearchableCountryDropdown";
import swal from "sweetalert";
import accreditationPrintingService from "../../../services/accreditationPrintingService";

const resolveImageUrl = (url) => {
  if (!url) return "";
  const backendBase = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
  if (url.includes(".s3.") || url.includes("amazonaws.com")) {
    return url;
  }
  if (url.includes("/uploads/")) {
    const uploadIndex = url.indexOf("/uploads/");
    const uploadPath = url.substring(uploadIndex);
    return backendBase ? `${backendBase}${uploadPath}` : uploadPath;
  }
  return url;
};

const Handover = () => {
  const [selected, setSelected] = useState(new Set());
  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserTypes, setSelectedUserTypes] = useState(() => {
    const saved = localStorage.getItem('handover_selectedUserTypes');
    if (!saved) return [];
    try { return JSON.parse(saved); } catch { return []; }
  });
  const [handoverType, setHandoverType] = useState(localStorage.getItem('handover_handoverType') || "");
  const [searchTerm, setSearchTerm] = useState(localStorage.getItem('handover_searchTerm') || "");
  const [userFrom, setUserFrom] = useState(localStorage.getItem('handover_userFrom') || "");
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedHandoverDetail, setSelectedHandoverDetail] = useState(null);
  const [selectedUserInfo, setSelectedUserInfo] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedImageAlt, setSelectedImageAlt] = useState("");
  const [page, setPage] = useState(1);
  const [data,setData]=useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [countryId, setCountryId] = useState(localStorage.getItem('handover_countryId') || "");
  const [sportId, setSportId] = useState(localStorage.getItem('handover_sportId') || "");
  const [accredStatus, setAccredStatus] = useState("");

  // Helper function to handle image clicks
  const handleImageClick = (imageUrl, imageAlt) => {
    setSelectedImage(imageUrl);
    setSelectedImageAlt(imageAlt);
    setShowImageModal(true);
  };

  // Helper function to check if a row is selectable (not handed over)
  const isRowSelectable = (row) => {
    return !row.handoverDetail && !row.accredStatus; // not handed over, not blocked/revoked
  };

  // Block / revoke / unblock one user's accreditation (allowed before or after handover)
  const fetchRef = useRef(() => {}); // always the latest fetchData, so the refresh keeps current filters
  const changeAccredStatus = useCallback(async (item, action) => {
    const name = [item.first_name, item.last_name].filter(Boolean).join(" ") || "this user";
    const copy = {
      block: ["Block accreditation?", `${name} will be unable to be handed over or printed until unblocked.`],
      revoke: ["Revoke accreditation?", `This is PERMANENT and cannot be undone. ${name}'s accreditation will be withdrawn.`],
      unblock: ["Unblock accreditation?", `${name}'s accreditation will be active again.`],
    }[action];
    const reason = await swal({
      title: copy[0],
      text: copy[1],
      icon: "warning",
      dangerMode: action !== "unblock",
      buttons: ["Cancel", action === "unblock" ? "Unblock" : action === "revoke" ? "Revoke" : "Block"],
      content: action === "unblock" ? undefined : { element: "input", attributes: { placeholder: "Reason (required)" } },
    });
    if (reason === null || reason === false) return;
    if (action !== "unblock" && !String(reason).trim()) return swal("Reason required", "Please enter a reason.", "error");
    try {
      await enhancedHandoverService.setAccredStatus(action, [item.registration_data_id], reason);
      await fetchRef.current();
    } catch (err) {
      swal("Failed", err.response?.data?.rejected?.[0]?.reason || err.response?.data?.message || getErrorMessage(err), "error");
    }
  }, []);

  // Email (or re-send) the person's accreditation card as a PDF to the address on their accreditation
  const emailCard = useCallback(async (item) => {
    const name = [item.first_name, item.last_name].filter(Boolean).join(" ") || "this person";
    const ok = await swal({
      title: "Email accreditation card?",
      text: `The card of ${name} will be emailed to ${item.email_id || "their address"}.`,
      icon: "info",
      buttons: ["Cancel", "Send"],
    });
    if (!ok) return;
    try {
      const res = await accreditationPrintingService.emailCard(item.registration_data_id);
      swal(res.success ? "Sent" : res.status === "skipped" ? "Not sent" : "Failed", res.message, res.success ? "success" : "warning");
    } catch (err) {
      swal("Failed", err.response?.data?.message || getErrorMessage(err), "error");
    }
  }, []);

  // Helper functions to persist filter changes
  const handleUserTypeChange = (values) => {
    setSelectedUserTypes(values || []);
    localStorage.setItem('handover_selectedUserTypes', JSON.stringify(values || []));
  };

  const handleHandoverTypeChange = (value) => {
    setHandoverType(value);
    localStorage.setItem('handover_handoverType', value);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    localStorage.setItem('handover_searchTerm', value);
  };

  const handleUserFromChange = (value) => {
    setUserFrom(value);
    localStorage.setItem('handover_userFrom', value);
  };

  const handleSportChange = (value) => {
    setSportId(value);
    localStorage.setItem('handover_sportId', value);
  };

  const handleCountryChange = (value) => {
    setCountryId(value);
    localStorage.setItem('handover_countryId', value);
  };

  // Reset all filters to initial state
  const handleResetFilters = () => {
    setSelectedUserTypes([]);
    setHandoverType("");
    setSearchTerm("");
    setUserFrom("");
    setCountryId("");
    setSportId("");
    setAccredStatus("");
    setPage(1);
    setSelected(new Set());
    
    // Clear localStorage
    localStorage.removeItem('handover_selectedUserTypes');
    localStorage.removeItem('handover_handoverType');
    localStorage.removeItem('handover_searchTerm');
    localStorage.removeItem('handover_userFrom');
    localStorage.removeItem('handover_sportId');
    localStorage.removeItem('handover_countryId');
  };



  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await enhancedHandoverService.getHandoverStats();
        if (response.success && response.data) {
          setSummary(response.data);
        } else {
          console.error("Failed to fetch summary:", response.message);
        }
      } catch (err) {
        console.error("Failed to fetch summary", err);
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      }
    };
    fetchSummary();
  }, []);


  useEffect(() => {
    const fetchUserTypes = async () => {
      try {
        const response = await enhancedHandoverService.getUserSubTypes();
        console.log("Handover - User sub-types API response:", response);
        
        if (response.success && response.data) {
          setUserTypes(response.data);
          console.log("Handover - User sub-types set successfully:", response.data);
        } else {
          throw new Error(response.message || "Failed to fetch user sub-types");
        }
      } catch (err) {
        console.error("Failed to fetch user sub-types", err);
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        setUserTypes([]);
      }
    };
    fetchUserTypes();
  }, []);



  // Clear selection only when filters change, not when page changes
  useEffect(() => {
    setSelected(new Set());
  }, [selectedUserTypes, handoverType, searchTerm, userFrom, countryId, sportId]);

  const stats = useMemo(() => {
     if (!summary) return [];
     return[
     {
       key: "printed",
       title: "Printed Record",
       value: summary.printedCount || 0,
       icon: <HugeiconsIcon icon={PrinterIcon} />,
     },
     {
       key: "handed",
       title: "Records Handedover",
       value: summary.handedOverCount || 0,
       icon: <HugeiconsIcon icon={ShareIcon} />,
     },
     {
       key: "pending",
       title: "Total Pending",
       value: summary.pendingCount || 0,
       icon: <HugeiconsIcon icon={ClockIcon} />,
     },
   ];
   }, [summary]);

  // Pending records count in current view (rows without handover detail)
  // const totalPendingCount = Array.isArray(data)
  //   ? data.filter((row) => !row.handoverDetail).length
  //   : 0;


  const handleExport = useCallback(async () => {
    try {
      // Map UI handoverType to API handover_type values
      let apiHandoverType;
      if (handoverType === "HANDED_OVER") apiHandoverType = "approved";
      else if (handoverType === "PENDING") apiHandoverType = "pending";
      else if (handoverType === "ALL") apiHandoverType = "all";

      const params = {
        handover_type: apiHandoverType,
        multi_sub_category: (selectedUserTypes && selectedUserTypes.length > 0) ? selectedUserTypes.join(',') : undefined,
        search: searchTerm || undefined,
        user_from: userFrom || "accred", // Default to "accred" when no selection
        country_id: countryId || undefined,
        sport_id: sportId || undefined,
      };

      const response = await enhancedHandoverService.exportHandoverData(params);
      if (response.success && response.data) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `handover_export_${timestamp}.xlsx`;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error(response.message || "Failed to export data");
      }
    } catch (err) {
      console.error("Export failed", err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    }
  }, [selectedUserTypes, handoverType, searchTerm, userFrom, countryId, sportId]);



  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Map UI handoverType to API handover_type values
      let apiHandoverType;
      if (handoverType === "HANDED_OVER") apiHandoverType = "approved";
      else if (handoverType === "PENDING") apiHandoverType = "pending";
      else if (handoverType === "ALL") apiHandoverType = "all";

      const params = {
        page,
        limit: pageSize === 'all' ? 100000 : pageSize,
        handover_type: apiHandoverType,
        multi_sub_category: (selectedUserTypes && selectedUserTypes.length > 0) ? selectedUserTypes.join(',') : undefined,
        search: searchTerm || undefined,
        user_from: userFrom || "accred", // Default to "accred" when no selection
        country_id: countryId || undefined,
        sport_id: sportId || undefined,
        accred_status: accredStatus || undefined,
      };

      const response = await enhancedHandoverService.getHandoverData(params);
      
      if (response.success && response.data) {
        const items = response.data;
        const formatted = items.map((item, index) => ({
          sno: (page - 1) * (pageSize === 'all' ? items.length : pageSize) + index + 1,
          id: item.registration_data_id,
          firstName: item.first_name || "",
          middleName: item.middle_name || "",
          lastName: item.last_name || "",
          email: item.email_id,
          phone: item.mobile_number,
          userPhoto: (
            <img
              src={resolveImageUrl(item.player_image_path) || image}
              alt="user"
              width={55}
              height={55}
              style={{ objectFit: "contain", cursor: "pointer" }}
              onClick={() => handleImageClick(resolveImageUrl(item.player_image_path) || image, "User Photo")}
            />
          ),
          idPhoto: (
            <img
              src={resolveImageUrl(item.office_id_image) || id}
              alt="id"
              width={55}
              height={55}
              style={{ objectFit: "contain", cursor: "pointer" }}
              onClick={() => handleImageClick(resolveImageUrl(item.office_id_image) || id, "ID Photo")}
            />
          ),
          userType: item.category_name,
          responsibleOrg: item.organisation,
          createdBy: item.created_by?.fullname || "N/A",
          handoverStatus: item.handover_detail ? "Handed Over" : "Pending",
          handoverDetail: item.handover_detail,
          accredStatus: item.revocation?.action || "",
          accredStatusCell: item.revocation ? (
            <span
              className={`badge ${item.revocation.action === "revoked" ? "bg-danger" : "bg-warning text-dark"}`}
              title={item.revocation.reason}
            >
              {item.revocation.action === "revoked" ? "Revoked" : "Blocked"}
            </span>
          ) : (
            <span className="badge bg-success">Active</span>
          ),
          accredActions: (
            <div className="d-flex gap-1 justify-content-center">
              {item.revocation?.action === "revoked" ? (
                <span className="text-muted small">Permanent</span>
              ) : (
                <>
                  {item.revocation ? (
                    <button className="btn btn-outline-success btn-sm" onClick={() => changeAccredStatus(item, "unblock")}>Unblock</button>
                  ) : (
                    <button className="btn btn-outline-warning btn-sm" onClick={() => changeAccredStatus(item, "block")}>Block</button>
                  )}
                  <button className="btn btn-outline-danger btn-sm" onClick={() => changeAccredStatus(item, "revoke")}>Revoke</button>
                </>
              )}
            </div>
          ),
          handOver: (
            <div className="d-flex gap-1 justify-content-center">
            {item.handover_detail ? (
            <button 
              className="btn btn-success btn-sm" 
              style={{ minWidth: '100px', fontSize: '12px' }}
              onClick={() => {
                setSelectedHandoverDetail(item.handover_detail);
                setSelectedUserInfo(item);
                setDetailsModalOpen(true);
              }}
            >
              Handed Over
            </button>
          ) : (
            <button 
              className="btn btn-primary btn-sm" 
              style={{ minWidth: '100px', fontSize: '12px' }}
              disabled={!!item.revocation}
              onClick={() => {
                setSelected((prev) => {
                  if (prev && prev.size > 0) {
                    return new Set(prev);
                  }
                  return new Set([item.registration_data_id]);
                });
                setModalOpen(true);
              }}
            >
              Hand Over
            </button>
            )}
            {String(item.registration_status) === "2" && Number(item.is_captured) === 1 && !item.disapproval_reason && !item.revocation && (
              <button
                className="btn btn-outline-primary btn-sm"
                style={{ fontSize: '12px' }}
                title="Email this person their accreditation card (PDF)"
                onClick={() => emailCard(item)}
              >
                Email card
              </button>
            )}
            </div>
          ),
        }));

        setData(formatted);
        setTotalPages(response.totalPages || 1);
        setTotalItems(response.totalItems || 0);
      } else {
        throw new Error(response.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error(err);
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [page, selectedUserTypes, handoverType, searchTerm, userFrom, pageSize, countryId, sportId, accredStatus, changeAccredStatus, emailCard]);

  fetchRef.current = fetchData;

  useEffect(() => {
    fetchData();
  }, [fetchData]);
 



  useEffect(() => {
    setPage(1);
  }, [userTypes, handoverType, searchTerm, userFrom, countryId, sportId]);

  const handlePageSizeChange = (e) => {
    const val = e.target.value;
    const parsed = val === 'all' ? 'all' : parseInt(val, 10);
    setPageSize(parsed);
    setPage(1);
  };

  const columns = [
    { key: "sno", header: "S No.", accessor: "sno", align: "center" },
    { key: "firstName", header: "First Name", accessor: "firstName", align: "center" },
    { key: "middleName", header: "Middle Name", accessor: "middleName", align: "center" },
    { key: "lastName", header: "Last Name", accessor: "lastName", align: "center" },
    { key: "email", header: "Email Id", accessor: "email", align: "center" },
    { key: "phone", header: "Phone", accessor: "phone", align: "center" },
    { key: "userPhoto", header: "User Photo", accessor: "userPhoto", align: "center" },
    { key: "idPhoto", header: "Id Photo", accessor: "idPhoto", align: "center" },
    { key: "userType", header: "User Type", accessor: "userType", align: "center" },
    {
      key: "responsibleOrg",
      header: "Responsible Organization",
      accessor: "responsibleOrg",
      align: "center"
    },
    { key: "handoverStatus", header: "Handover Status", accessor: "handoverStatus", align: "center" },
    { key: "accredStatusCell", header: "Accreditation", accessor: "accredStatusCell", align: "center" },
    { key: "createdBy", header: "Created By", accessor: "createdBy", align: "center" },
    { key: "accredActions", header: "Block / Revoke", accessor: "accredActions", align: "center" },
    { key: "handOver", header: "Hand Over", accessor: "handOver", align: "center" },
  ];

  console.log("Handover - Current userSubTypes state:", userTypes);

  return (
    <>
      <div className="card">
        <PageTitle motherMenu={"Handover"} activeMenu={"Handover"} />
        <div>
          <div className="row mx-3 ">
            {stats.map((s) => (
              <div className="col-md-4" key={s.key}>
                <StatCard title={s.title} value={s.value} icon={s.icon} />
              </div>
            ))}
          </div>
        </div>

        <div className="col-12">
          <div>
            <div className="card-header border-bottom-0">
              <div className="row g-2 align-items-end">
                <div className="col-lg-1 col-md-2">
                  <label className="form-label small text-muted mb-1">Items</label>
                  <select className="form-select form-select-sm" value={pageSize} onChange={handlePageSizeChange}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={'all'}>All</option>
                  </select>
                </div>
                <div className="col-lg-3 col-md-4">
                  <label className="form-label small text-muted mb-1">User Types</label>
                  <SearchableUserTypeMultiSelect
                    userTypes={userTypes}
                    values={selectedUserTypes}
                    onChange={handleUserTypeChange}
                    placeholder="All User Types"
                    className="form-control form-control-sm select-brand"
                    filterActiveOnly={true}
                  />
                </div>
                <div className="col-lg-2 col-md-3">
                  <label className="form-label small text-muted mb-1">Handover Type</label>
                  <SearchableHandoverTypeDropdown
                    value={handoverType}
                    onChange={handleHandoverTypeChange}
                    placeholder="Select"
                    required={false}
                    className="form-select form-select-sm select-brand"
                    showCount={false}
                    maxHeight="200px"
                  />
                </div>
                <div className="col-lg-2 col-md-3">
                  <label className="form-label small text-muted mb-1">Data From</label>
                  <SearchableUserFromDropdown
                    value={userFrom}
                    onChange={handleUserFromChange}
                    placeholder="Select"
                    required={false}
                    className="form-select form-select-sm select-brand"
                    showCount={false}
                    maxHeight="200px"
                  />
                </div>
                <div className="col-lg-2 col-md-3">
                  <label className="form-label small text-muted mb-1">State</label>
                  <SearchableCountryDropdown
                    value={countryId}
                    onChange={handleCountryChange}
                    placeholder="Select State"
                    required={false}
                    className="form-select form-select-sm select-brand"
                    showCount={false}
                    maxHeight="200px"
                  />
                </div>
                <div className="col-lg-2 col-md-3">
                  <label className="form-label small text-muted mb-1">Sport</label>
                  <SearchableSportDropdown
                    value={sportId}
                    onChange={handleSportChange}
                    placeholder="Select Sport"
                    required={false}
                    className="form-select form-select-sm select-brand"
                    showCount={false}
                    maxHeight="200px"
                  />
                </div>
                <div className="col-lg-2 col-md-3">
                  <label className="form-label small text-muted mb-1">Accreditation</label>
                  <select
                    className="form-select form-select-sm"
                    value={accredStatus}
                    onChange={(e) => { setAccredStatus(e.target.value); setPage(1); }}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                    <option value="revoked">Revoked</option>
                  </select>
                </div>
                <div className="col-lg-2 col-md-3">
                  <label className="form-label small text-muted mb-1">Search</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>
                <div className="col-lg-6 col-md-9 d-flex justify-content-start align-items-center gap-2">
                  <button 
                    className="btn btn-primary" 
                    onClick={handleResetFilters}
                    title="Reset all filters"
                    style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <i className="fas fa-undo me-1"></i>
                    Reset Filters
                  </button>
                  <button 
                    className="btn btn-outline-primary" 
                    disabled={!selected || selected.size === 0} 
                    onClick={() => setModalOpen(true)}
                    style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Handover Record
                  </button>
                  <a className="btn btn-outline-primary" href="/scanner" target="_blank" rel="noreferrer" style={{ height: 40, display: 'flex', alignItems: 'center' }}>
                    Open Scanner
                  </a>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleExport}
                    style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Export
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              {loading ? (<p>Loading...</p>):(<>
               <DataTable
                columns={columns}
                data={data}
                rowKey="id"
                selectable
                selectedRowIds={selected}
                onSelectChange={setSelected}
                isRowSelectable={isRowSelectable}
                stickyHeader
                maxBodyHeight={420}
              />
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={pageSize === 'all' ? totalItems : pageSize}
                onPageChange={setPage}
              />
              </>)}
              
            </div>
          </div>
        </div>
      </div>
      <HandoverFlowModal
        open={modalOpen}
        selectedCount={selected?.size || 0}
        loading={submitting}
        onClose={() => setModalOpen(false)}
        onConfirm={async (pocName, pocContact, issuedByName) => {
          try {
            setSubmitting(true);
            const ids = Array.from(selected);
            const payload = {
              poc_name: pocName,
              poc_contact: pocContact,
              issued_by_name: issuedByName,
              registration_process_id: ids
            };
            const response = await enhancedHandoverService.createLegacy(payload);
            const pdfUrl = response?.data?.handover?.pdf || response?.data?.handover?.pdf_link || response?.data?.pdf || response?.data?.pdf_link;
            if (response.success || pdfUrl) {
              // Open PDF URL in a new tab if available
              if (pdfUrl) {
                window.open(pdfUrl, '_blank');
              }
              // refresh data and close modal
              setSelected(new Set());
              setModalOpen(false);
              await fetchData();
              // Also refresh summary stats
              const summaryResponse = await enhancedHandoverService.getHandoverStats();
              if (summaryResponse.success && summaryResponse.data) {
                setSummary(summaryResponse.data);
              }
              await swal({
                title: "Handover completed",
                text: "Cards have been handed over successfully.",
                icon: "success",
                buttons: {
                  confirm: {
                    text: "OK",
                    className: "btn btn-primary",
                  }
                }
              });
            } else {
              await swal({
                title: "Handover failed",
                text: response.message && response.message !== 'Legacy handover created' ? response.message : 'Failed to handover',
                icon: "error",
              });
            }
          } catch (e) {
            console.error(e);
            const errorMessage = getErrorMessage(e);
            await swal({
              title: "Handover failed",
              text: errorMessage,
              icon: "error",
            });
          } finally {
            setSubmitting(false);
          }
        }}
      />
      <HandoverDetailsModal
        open={detailsModalOpen}
        handoverDetail={selectedHandoverDetail}
        userInfo={selectedUserInfo}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedHandoverDetail(null);
          setSelectedUserInfo(null);
        }}
      />
      <ImageModal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        imageUrl={selectedImage}
        imageAlt={selectedImageAlt}
      />
    </>
  );
};

export default Handover;
